import React, { useState, useEffect } from "react";
import { clearSession } from "../shared/_helpers/session";
import DatePicker from "react-datepicker";
import Dropdown from 'react-bootstrap/Dropdown'
import { Link, useNavigate, useParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import { getAdvisorAvailability, updateAdvisorAvailability, getOffDates, addOffDate, removeOffDate } from "../shared/_services/common.services";
import Loader from "./Loader";
import momentTz from "moment-timezone";

const AdvisorManageAvailability = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [fromTime, setFromTime] = useState(new Date());
  const [toTime, setToTime] = useState(new Date());
  const [fromBreakTime, setFromBreakTime] = useState(new Date());
  const [toBreakTime, setToBreakTime] = useState(new Date());
  const [showDropdown, setShowDropdown] = useState(false);
  const [startDate, setStartDate] = useState(new Date());
  const [advisorData, setAdvisorData] = useState([]);
  const [offDates, setOffDates] = useState([]);
  const [showAvailabilityLoader, setShowAvailabilityLoader] = useState(false);
  const params = useParams();
  const [selectedDay, setSelectedDay] = useState(null);
  const [isClosed, setIsClosed] = useState(false);
  const [availabilityId, setAvailabilityId] = useState(null);
  const navigate = useNavigate();

  //get advisor availability details
  const getAdvisorData = async () => {
    setShowAvailabilityLoader(true);
    let data = await getAdvisorAvailability(params?.advisorId || JSON.parse(localStorage.getItem('currentUser'))?.user?._id);
    if (data?.status === 200 && data?.data?.availability?.length > 0) {
      setAdvisorData(data?.data?.availability[0]);
      setAvailabilityId(data?.data?.availability[0]?._id);
      const day = data?.data?.availability[0]?.days?.find(d => d.weekday === selectedDay?.weekday);
      if (day) {
        setIsClosed(day.is_closed);
      }
    }
    else {
      if (data?.response?.status === 401) {
        clearSession();
        enqueueSnackbar("Your session has been expired!", {
          variant: 'error',
          anchorOrigin: { horizontal: 'right', vertical: 'top' },
        });
        navigate('/login');
      }
    }
    setShowAvailabilityLoader(false);
  };

  //get off date
  const getAdvisorOffDates = async () => {
    setShowAvailabilityLoader(true);
    let data = await getOffDates(params?.advisorId ? params?.advisorId : JSON.parse(localStorage.getItem('currentUser'))?.user?._id);
    if (data?.status === 200) {
      setOffDates([...data.data]);
    }
    setShowAvailabilityLoader(false);
  };

  useEffect(() => {
    getAdvisorData();
    getAdvisorOffDates();
  }, []);

  //add new off date
  const handleOffDateAdd = async () => {
    const advisorId = JSON.parse(localStorage.getItem('currentUser'))?.user?._id;
    const date = startDate.toISOString().split("T")[0];
    setShowAvailabilityLoader(true);
    const response = await addOffDate(advisorId, date);
    if (response?.status === 201) {
      enqueueSnackbar('Off day added successfully!', {
        variant: 'success'
      });
      getAdvisorOffDates();
      setShowDropdown(false);
      setStartDate(new Date());
    } else {
      enqueueSnackbar('Failed to add off day', {
        variant: 'error'
      });
      setShowDropdown(true);
    }
    setShowAvailabilityLoader(false);
  };

 

  //update day
  const updateDays =  async () => {
    if (fromTime >= toTime) {
      enqueueSnackbar('The "From" time cannot be the same as or later than the "To" time.', {
        variant: 'error',
        anchorOrigin: { horizontal: 'right', vertical: 'top' },
      });
      return;
    }
  
    // Prepare the updated day object
    const updatedDay = {
      weekday: selectedDay.weekday, // Ensure you are updating the correct weekday
      startHour: fromTime.getHours(),
      startMinute: fromTime.getMinutes(),
      endHour: toTime.getHours(),
      endMinute: toTime.getMinutes(),
      is_closed: isClosed,
    };
  
    const response = await updateAdvisorAvailability(availabilityId, {
      day: updatedDay, 
      timezone: momentTz.tz.guess(true),
    });
  
    if (response?.status === 200) {
      getAdvisorData();
      enqueueSnackbar('Availability updated successfully!', { variant: 'success' });
    } else {
      enqueueSnackbar('Failed to update availability', { variant: 'error' });
    }
  };
  

  //add new break
  const handleAddBreak = async () => {
    if (fromBreakTime >= toBreakTime) {
      enqueueSnackbar('The "From" break time cannot be the same as or later than the "To" break time.', {
        variant: 'error',
        anchorOrigin: { horizontal: 'right', vertical: 'top' },
      });
      return;
    }
  
    const newBreak = {
      startHour: fromBreakTime.getHours(),
      startMinute: fromBreakTime.getMinutes(),
      endHour: toBreakTime.getHours(),
      endMinute: toBreakTime.getMinutes()
    };
  
    const response = await updateAdvisorAvailability(availabilityId, {
      advisorId: JSON.parse(localStorage.getItem('currentUser'))?.user?._id,
      breaks: [newBreak], // Pass new break to add
      timezone: momentTz.tz.guess(true)
    });
  
    if (response?.status === 200) {
      getAdvisorData();
      enqueueSnackbar('Break added successfully!', { variant: 'success' });
    } else {
      enqueueSnackbar('Failed to add break', { variant: 'error' });
    }
  };
  

  //remove break
  const handleRemoveBreak = async (breakIndex) => {
    const breakToDelete = advisorData?.breaks[breakIndex];
  
    // Ensure that breakToDelete has the same structure as the breaks in the database
  
    const response = await updateAdvisorAvailability(availabilityId, {
      removeBreak: [breakToDelete], // Pass break to delete
      timezone: momentTz.tz.guess(true)
    });
  
    if (response?.status === 200) {
      getAdvisorData();
      enqueueSnackbar('Break deleted successfully!', { variant: 'success' });
    } else {
      enqueueSnackbar('Failed to delete break', { variant: 'error' });
    }
};


  //remove off date
  const handleRemoveOffDate = async (offDayId) => {
    setShowAvailabilityLoader(true);
    const data = await removeOffDate(offDayId);
    if (data?.status === 200) {
      enqueueSnackbar('Off day removed successfully!', { variant: 'success' });
      getAdvisorOffDates();
    } else {
      enqueueSnackbar('Failed to remove off day', { variant: 'error' });
    }
    setShowAvailabilityLoader(false);
  };


  // Function to convert 24-hour time to 12-hour time with AM/PM
  const convertTime = (hour, minute) => {
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    const minutePadded = minute.toString().padStart(2, '0');
    return `${hour12}:${minutePadded} ${ampm}`;
  };

  const weekdays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  const handleDayClick = (day) => {
    setSelectedDay(day);
    setFromTime(new Date(1970, 0, 1, day.startHour, day.startMinute));
    setToTime(new Date(1970, 0, 1, day.endHour, day.endMinute));
    setIsClosed(day.is_closed);
  };

  const formatOffDay = (dateString) => {
  
    const dateParts = dateString.split('-'); // ["2025", "02", "26"]
    const date = new Date(Date.UTC(dateParts[0], dateParts[1] - 1, dateParts[2])); // Ensure UTC parsing
  
    const weekdays = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
  
    return {
      day: date.getUTCDate(),
      month: date.toLocaleString('default', { month: 'long', timeZone: 'UTC' }), // Ensure UTC month
      year: date.getUTCFullYear(),
      weekday: weekdays[date.getUTCDay()], // Get correct weekday
    };
  };
  

  return (
    <>
      {showAvailabilityLoader ? <Loader /> : <div className="ManageAvailability_screen">
        <div className="d-flex mt-3 mb-2">
          <Link to="/advisor-meetings" className="common-back-btn mr-3">
            <i className="fa fa-angle-left"></i>
          </Link>
          <h2 className="page-heading">Manage Availability</h2>
        </div>
        <div className="slot-days-list mini-scroll-bar mb-4">
          {advisorData?.days?.map((day) => (
            <div
              key={day.weekday}
              className={`slot-day ${selectedDay?.weekday === day.weekday ? 'if_selected' : ''}`}
              onClick={() => handleDayClick(day)}
            >
              <p className="mb-2">{weekdays[day.weekday]}</p>
              {day.is_closed ? (
                <p>Closed</p>
              ) : (
                <p>
                  {convertTime(day.startHour, day.startMinute)} to {convertTime(day.endHour, day.endMinute)}
                </p>
              )}
            </div>
          ))}
        </div>

        {/* edit day section */}
        <div className="row">
          <div className="col-md-12">
            <div className="ast-card mb-5">
              <form action="">
                <div className="add-availability-form">
                  <h5>{selectedDay ? weekdays[selectedDay.weekday] : "Select a day"}</h5>
                  <div className="avblt-mini-card">
                    <label className="text-mini w-100">
                      From
                    </label>
                    <div className="cust-timepicker">
                      <DatePicker
                        selected={fromTime}
                        onChange={(fromTime) => setFromTime(fromTime)}
                        showTimeSelect
                        showTimeSelectOnly
                        timeIntervals={30}
                        dateFormat="h:mm aa"
                      />
                    </div>
                  </div>
                  <div className="time-arrow">
                    <svg width="60" height="17" viewBox="0 0 60 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path fillRule="evenodd" clipRule="evenodd" d="M55.8041 7.9392L49.9297 2.06667L51.2513 0.746933L59.3769 8.87253L51.2513 17L49.9297 15.6784L55.8041 9.80587L0.357178 9.80587V7.9392L55.8041 7.9392Z" fill="#63363A" />
                    </svg>
                  </div>
                  <div className="avblt-mini-card">
                    <label className="text-mini w-100">
                      To
                    </label>
                    <div className="cust-timepicker">
                      <DatePicker
                        selected={toTime}
                        onChange={(toTime) => setToTime(toTime)}
                        showTimeSelect
                        showTimeSelectOnly
                        timeIntervals={30}
                        dateFormat="h:mm aa"
                      />
                    </div>
                  </div>
                  <div className="select-open-close">
                    <div id="open" onClick={() => setIsClosed(false)} className={`btn ${isClosed ? '' : 'if_selected'}`}>Open</div>
                    <div id="close" onClick={() => setIsClosed(true)} className={`btn ${isClosed ? 'if_selected' : ''}`}>Close</div>
                  </div>
                  <button type="button" onClick={updateDays} className="as-btn-mini">Update</button>
                </div>
              </form>
            </div>
          </div>


          {/* break hours section */}
          <div className="col-md-6">
            <div className="ast-card">
              <div className="d-flex justify-content-between mb-4">
                <h4 className="mt-2">Set Break Hours</h4>
                <button type="button" onClick={handleAddBreak} className="as-btn-mini">Add</button>
              </div>
              <div className="d-flex justify-content-between place-items-center gap-10">
                <div className="avblt-mini-card">
                  <label className="text-mini w-100">
                    From
                  </label>
                  <div className="cust-timepicker break-picker">
                    <DatePicker
                      selected={fromBreakTime}
                      onChange={(fromBreakTime) => setFromBreakTime(fromBreakTime)}
                      showTimeSelect
                      showTimeSelectOnly
                      timeIntervals={30}
                      dateFormat="h:mm aa"
                    />
                  </div>
                </div>
                <div className="time-arrow">
                  <svg width="60" height="17" viewBox="0 0 60 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path fillRule="evenodd" clipRule="evenodd" d="M55.8041 7.9392L49.9297 2.06667L51.2513 0.746933L59.3769 8.87253L51.2513 17L49.9297 15.6784L55.8041 9.80587L0.357178 9.80587V7.9392L55.8041 7.9392Z" fill="#63363A" />
                  </svg>
                </div>
                <div className="avblt-mini-card">
                  <label className="text-mini w-100">
                    To
                  </label>
                  <div className="cust-timepicker break-picker">
                    <DatePicker
                      selected={toBreakTime}
                      onChange={(toBreakTime) => setToBreakTime(toBreakTime)}
                      showTimeSelect
                      showTimeSelectOnly
                      timeIntervals={30}
                      dateFormat="h:mm aa"
                    />
                  </div>
                </div>
              </div>
              <div className="break-hours-list mini-scroll-bar mt-4">
                {advisorData?.breaks?.length > 0 ? (
                  advisorData?.breaks.map((key, index) => (
                    <div className="break-hour-item" key={`${key.startHour}-${key.startMinute}-${key.endHour}-${key.endMinute}`}>
                      <div className="avblt-mini-card">
                        <p className="text-blue-light mb-2">Break {index + 1} :</p>
                        <p className="text-blue-light">
                          {convertTime(key.startHour, key.startMinute)} to {convertTime(key.endHour, key.endMinute)}
                        </p>
                      </div>
                      <button className="remove-item-btn" onClick={() => handleRemoveBreak(index)} title="Remove Break">
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M6.99998 12.3333C4.05998 12.3333 1.66665 9.93998 1.66665 6.99998C1.66665 4.05998 4.05998 1.66665 6.99998 1.66665C9.93998 1.66665 12.3333 4.05998 12.3333 6.99998C12.3333 9.93998 9.93998 12.3333 6.99998 12.3333ZM6.99998 0.333313C3.31331 0.333313 0.333313 3.31331 0.333313 6.99998C0.333313 10.6866 3.31331 13.6666 6.99998 13.6666C10.6866 13.6666 13.6666 10.6866 13.6666 6.99998C13.6666 3.31331 10.6866 0.333313 6.99998 0.333313ZM8.72665 4.33331L6.99998 6.05998L5.27331 4.33331L4.33331 5.27331L6.05998 6.99998L4.33331 8.72665L5.27331 9.66665L6.99998 7.93998L8.72665 9.66665L9.66665 8.72665L7.93998 6.99998L9.66665 5.27331L8.72665 4.33331Z" fill="#6B7897" />
                        </svg>
                      </button>
                    </div>
                  ))
                ) : (
                  <h4 className="mt-4">No break time added</h4>
                )}
              </div>

            </div>
          </div>


          {/* off days section */}
          <div className="col-md-6">
            <div className="ast-card">
              <div className="d-flex justify-content-between mb-4">
                <h4 className="mb-0 py-2">Off Day</h4>
                <Dropdown show={showDropdown} onToggle={(isOpen) => setShowDropdown(isOpen)}>
                  <Dropdown.Toggle className="as-btn-mini">
                    Add
                  </Dropdown.Toggle>
                  <Dropdown.Menu className="off-day-picker">
                    <div className="appointment-date-paicker">
                      <DatePicker
                        inline
                        selected={startDate}
                        onChange={(date) => setStartDate(date)}
                        minDate={new Date()}
                      />
                    </div>
                    <Dropdown.Divider />
                    <div className="p-2 d-flex justify-content-end">
                      <button className="as-btn-mini" onClick={handleOffDateAdd}>Add</button>
                    </div>
                  </Dropdown.Menu>
                </Dropdown>
              </div>

              <div className="offday-list mini-scroll-bar">
                {/* loop this offday-item */}
                {offDates.length > 0 ? (
                  offDates.map((offDate) => {
                    const formattedOffDay = formatOffDay(offDate.date);
                    
                    return (
                      <div className="offday-item" key={offDate.id}>
                        <div className="avblt-mini-card">
                          <div className="d-flex place-items-center">
                            <h2 id="off_day">{formattedOffDay.day}</h2>
                            <div>
                              <p id="off_day_month" className="text-blue-light mb-1">
                                {formattedOffDay.month} {formattedOffDay.year}
                              </p>
                              <small id="off_day_week" className="text-blue-light">
                                {formattedOffDay.weekday}
                              </small>
                            </div>
                          </div>
                        </div>
                        <button className="remove-item-btn" title="Remove Off Day" onClick={() => handleRemoveOffDate(offDate._id)}>
                          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M6.99998 12.3333C4.05998 12.3333 1.66665 9.93998 1.66665 6.99998C1.66665 4.05998 4.05998 1.66665 6.99998 1.66665C9.93998 1.66665 12.3333 4.05998 12.3333 6.99998C12.3333 9.93998 9.93998 12.3333 6.99998 12.3333ZM6.99998 0.333313C3.31331 0.333313 0.333313 3.31331 0.333313 6.99998C0.333313 10.6866 3.31331 13.6666 6.99998 13.6666C10.6866 13.6666 13.6666 10.6866 13.6666 6.99998C13.6666 3.31331 10.6866 0.333313 6.99998 0.333313ZM8.72665 4.33331L6.99998 6.05998L5.27331 4.33331L4.33331 5.27331L6.05998 6.99998L4.33331 8.72665L5.27331 9.66665L6.99998 7.93998L8.72665 9.66665L9.66665 8.72665L7.93998 6.99998L9.66665 5.27331L8.72665 4.33331Z" fill="#6B7897" />
                          </svg>
                        </button>
                      </div>
                    );
                  })
                ) : (
                  <h4 className="mt-5">No off days added</h4>
                )}
              </div>

            </div>
          </div>
        </div>
      </div>}

    </>
  );
};

export default AdvisorManageAvailability;
